import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../../../core/services/api.service';
import { AuthService } from '../../../core/services/auth.service';
import { CenterService } from '../../../core/services/center.service';
import { PageTitleService } from '../../../core/services/page-title.service';
import { VioTablesV2Module } from 'vio-tables-v2';
import { CLOSED_MEMBERS_TABLE_CONFIG } from '../table-configs';
import { DateFormatService } from '../../../core/services/date-format.service';

type DatePreset = 'all' | 'month' | 'year' | 'custom';

const NOT_RECORDED = 'Not recorded';

/**
 * Closed memberships with their closure reason. Built on getMembers() (served
 * from the server's in-memory member cache) and filtered client-side, so no
 * dedicated endpoint is needed.
 */
@Component({
  selector: 'app-closed-members-report',
  standalone: true,
  imports: [CommonModule, FormsModule, VioTablesV2Module],
  templateUrl: './closed-members-report.component.html',
  styleUrl: './receipt-payment-report.component.scss',
})
export class ClosedMembersReportComponent implements OnInit {

  isManager = false;

  preset: DatePreset = 'all';
  customFrom = '';
  customTo   = '';

  loading = false;
  error   = '';
  loaded  = false;

  /** All closed members (already scoped to the manager's center), mapped to report rows. */
  private allClosed: any[] = [];
  /** Rows shown in the table (date filter; filter by reason via the table's right-click value filter). */
  records: any[] = [];

  tableConfig = CLOSED_MEMBERS_TABLE_CONFIG;

  constructor(
    private api: ApiService,
    private auth: AuthService,
    private centerSvc: CenterService,
    private pageTitle: PageTitleService,
    private dateFmt: DateFormatService,
  ) {}

  ngOnInit(): void {
    this.tableConfig = { ...this.tableConfig, dateDisplayFormat: this.dateFmt.current };
    this.pageTitle.set('Reports', 'Closed memberships and closure reasons');
    this.isManager = this.auth.getCurrentUser()?.role?.toLowerCase() === 'manager';

    const today = this.fmt(new Date());
    this.customFrom = `${today.slice(0, 7)}-01`;
    this.customTo   = today;
    this.load();
  }

  private fmt(d: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }

  private toDateStr(v: any): string {
    if (!v) return '';
    const d = new Date(v);
    return isNaN(d.getTime()) ? '' : this.fmt(d);
  }

  private isClosed(m: any): boolean {
    const st = String(m?.status || '').trim().toUpperCase();
    return st === 'CLOSED' || st === 'CLOSE' || st === 'CLOCE';
  }

  load(): void {
    this.loading = true;
    this.error   = '';
    this.loaded  = false;

    this.api.getMembers().subscribe({
      next: (members: any[]) => {
        let list = (members || []).filter(m => this.isClosed(m));
        if (this.isManager) {
          const area = this.centerSvc.selectedCenter?.area?.trim().toLowerCase();
          if (area) list = list.filter(m => (m.area || '').trim().toLowerCase() === area);
        }
        this.allClosed = list
          .map(m => {
            const cd = m.closureDetails || {};
            return {
              _id:           m._id,
              memNo:         m.memNo || m.memberSrNo || '',
              name:          m.name || '',
              phone:         m.phone || '',
              area:          m.area || '',
              memberType:    m.memberType === 'H' ? 'Door Delivery' : 'At Center',
              closedOn:      this.toDateStr(m.closedAt || cd.closedAt),
              closureReason: (cd.closureReason || '').trim() || NOT_RECORDED,
              remarks:       cd.refundRemarks || '',
              refundAmount:  cd.refundAmount ?? null,
              refundMode:    cd.refundMode || '',
              refundDate:    this.toDateStr(cd.refundDate || m.depositReturnDate),
            };
          })
          // Most recent closures first; undated (legacy) closures last.
          .sort((a, b) => (b.closedOn || '').localeCompare(a.closedOn || '') || a.name.localeCompare(b.name));
        this.loading = false;
        this.loaded  = true;
        this.applyFilters();
      },
      error: (err: any) => {
        this.error   = err?.message || 'Failed to load members.';
        this.loading = false;
      },
    });
  }

  setPreset(p: DatePreset): void {
    this.preset = p;
    if (p !== 'custom') this.applyFilters();
  }

  private activeRange(): { from: string; to: string } | null {
    const today = new Date();
    const t = this.fmt(today);
    if (this.preset === 'month')  return { from: `${t.slice(0, 7)}-01`, to: t };
    if (this.preset === 'year')   return { from: `${today.getFullYear()}-01-01`, to: t };
    if (this.preset === 'custom' && this.customFrom && this.customTo) return { from: this.customFrom, to: this.customTo };
    return null; // all time
  }

  applyFilters(): void {
    const range = this.activeRange();
    // A date range excludes legacy closures with no recorded close date.
    this.records = range
      ? this.allClosed.filter(r => r.closedOn && r.closedOn >= range.from && r.closedOn <= range.to)
      : this.allClosed;
  }

  /** Deposit refunded across the closed-date range (table filters/search not applied — the grand total row covers those). */
  get totalRefunded(): number {
    return this.records.reduce((sum, r) => sum + (Number(r.refundAmount) || 0), 0);
  }
}
